import type {
  Arc2D,
  Circle2D,
  Drawing2D,
  Point2,
  Polyline2D,
} from './viewTypes'

// ---------------------------------------------------------------------------
// Reconhecimento de círculos e arcos numa polilinha 2D.
//
// PORQUÊ. O IFC descreve um pilar redondo com um IfcCircleProfileDef, mas
// o caminho IFC -> Fragments tessela tudo em triângulos (o web-ifc corta
// o círculo em CIRCLE_SEGMENTS lados). Quando o corte 2D atravessa esse
// sólido, o contorno que sai é um POLÍGONO, e o desenho fica com uma laje
// circular de 16 lados e pilares hexagonais. Aumentar a tesselação só
// adia o problema (e não resolve nada num .frag já convertido): a solução
// de desenho é reconhecer o círculo no polígono e escrevê-lo como
// círculo/arco nos formatos que os têm (SVG `A`, PDF Béziers, DXF
// CIRCLE/ARC).
//
// COMO. Duas passagens sobre a polilinha:
//
//  1. pré-filtro barato pelos ÂNGULOS DE VIRAGEM. Num polígono inscrito
//     num círculo o ângulo exterior de cada vértice é igual ao ângulo ao
//     centro do lado seguinte, portanto uma sequência co-circular
//     traduz-se em viragens do MESMO SINAL e APROXIMADAMENTE IGUAIS.
//     Isto separa em O(n) os troços candidatos dos troços rectos, e
//     nunca deixa passar cantos vivos.
//
//  2. ajuste de círculo por mínimos quadrados (método de Kasa, sobre
//     coordenadas centradas no centróide para não perder algarismos) a
//     cada troço candidato, com verificação do resíduo máximo. Se falhar,
//     encolhe-se o troço pela ponta e tenta-se outra vez.
//
// GUARDA ANTI-FALSO-POSITIVO (a parte que interessa). Os vértices de um
// polígono regular AUTORAL caem exactamente num círculo: o resíduo é
// ZERO e não distingue um hexágono desenhado de propósito de um círculo
// tesselado. Só a CONTAGEM distingue. Por isso:
//   - um loop fechado com menos de `CIRCLE_MIN_POINTS` (8) vértices não
//     é sequer analisado: um hexágono fica hexágono, um rectângulo fica
//     rectângulo;
//   - nenhuma viragem de um arco pode passar de `MAX_STEP_RAD` (50 graus),
//     o que exclui à cabeça os fragmentos de hexágono (60 graus) e de
//     pentágono (72 graus). Um octógono (45 graus) passa: é o preço de
//     aceitar círculos com 8 lados, como pedido.
//
// VERIFICAÇÃO DO ARCO ESCOLHIDO (a rede que apanha o resto). O resíduo
// do ajuste só olha para os vértices que ENTRARAM no ajuste, e esses são
// só os cantos: os vértices deitados fora por serem colineares ficam de
// fora da conta. Se um deles estiver a meio de uma RECTA longa, o ajuste
// pode "saltar" a recta e o arco desenhado sai a 5 cm do caminho, a
// cruzar o resto do contorno (as lentes dos varões e dos fillets). Por
// isso, depois de escolhido o centro/raio/sentido, o arco é confrontado
// com o caminho que substitui, TODOS os vértices incluídos:
//   (a) cada vértice original tem de estar perto do arco, POR ORDEM ao
//       longo dele e dentro da sua amplitude;
//   (b) o arco amostrado tem de estar perto da polilinha original.
// A tolerância é a do ajuste MAIS a flecha da maior corda do troço
// (`r*(1-cos(passo/2))`), que é o desvio que a tesselação já tem por
// construção: aceita um octógono, recusa um desvio grosseiro.
//
// PRINCÍPIO: o reconhecimento é PURAMENTE GEOMÉTRICO. Não há aqui (nem
// pode haver) um único ramo por categoria IFC: um varão, um cotovelo de
// tubagem e um fillet de perfil metálico passam todos pelo mesmo código
// e são julgados pelos mesmos números (contagem de cantos, sinal das
// viragens, resíduo relativo ao raio, verificação por amostragem). O
// limiar de >= 5 cantos é EVIDÊNCIA ESTATÍSTICA, não um caso especial:
// abaixo disso a tesselação não chega para distinguir uma curva de um
// canto autoral.
//
// REGRA DE OURO: na dúvida, segmentos. Um arco não reconhecido é um
// defeito cosmético; um arco reconhecido a mais é o desenho errado.
//
// Tudo puro: números e arrays, sem DOM e sem three.
// ---------------------------------------------------------------------------

/** Número mínimo de vértices de uma sequência para poder virar arco. */
export const ARC_MIN_POINTS = 5

/** Número mínimo de vértices de um loop fechado para poder virar
 *  círculo. Abaixo disto não se analisa NADA (ver guarda acima). */
export const CIRCLE_MIN_POINTS = 8

/** Resíduo máximo absoluto do ajuste, em metros. */
export const ARC_TOL_M = 0.002

/** Resíduo máximo relativo ao raio. */
export const ARC_TOL_REL = 0.005

/** Viragem mínima por segmento, em radianos (~0,57 graus). Abaixo disto
 *  é ruído numérico de uma recta, não a tesselação de uma curva. */
export const MIN_STEP_RAD = 0.01

/** Viragem máxima por segmento, em radianos (50 graus). Ver guarda. */
export const MAX_STEP_RAD = (50 * Math.PI) / 180

/** Amplitude máxima de UM arco: meia volta.
 *
 *  Não é uma limitação do formato (o SVG tem a bandeira `large-arc`, o
 *  DXF não tem limite nenhum): é uma limitação DELIBERADA. Acima de meia
 *  volta o par (ponto inicial, ponto final) deixa de determinar o arco e
 *  qualquer engano de sentido manda o traço pelo outro lado do círculo,
 *  que é exactamente o artefacto em forma de lente. Uma curva maior sai
 *  em dois arcos, que desenham o mesmo. */
export const MAX_ARC_SWEEP = Math.PI * (1 + 1e-9)

/** Razão máxima entre a maior e a menor viragem do mesmo troço. A
 *  tesselação de uma curva é uniforme; a tolerância é generosa para
 *  aguentar os vértices extra que o corte introduz. */
export const STEP_RATIO = 3

/** Tolerância da soma das viragens de um loop fechado contra 2*pi. */
const TURN_SUM_TOL = 0.05

/** Abaixo desta viragem (radianos) o vértice está em cima da recta dos
 *  vizinhos e não conta para a forma.
 *
 *  Isto NÃO é cosmética, é o que faz o reconhecimento funcionar com
 *  geometria a sério: um cilindro tesselado em 16 faces chega ao corte
 *  como 16 quadriláteros, cada um partido em DOIS triângulos, e cada
 *  triângulo dá o seu segmento. O ponto onde a diagonal do quadrilátero
 *  atravessa o plano fica a meio da corda: o contorno sai com 32
 *  vértices, alternando cantos de 22,5 graus com vértices de 0 graus, e
 *  os 16 do meio nem sequer estão sobre a circunferência (estão a
 *  cos(11,25 graus) do centro, ou seja 2% para dentro). Ajustar o
 *  círculo com eles dentro estoirava o resíduo. */
const COLLINEAR_RAD = 1e-3

const TWO_PI = Math.PI * 2

/** Resíduo aceite para um raio. */
export function arcTolerance(r: number): number {
  return Math.max(ARC_TOL_M, ARC_TOL_REL * r)
}

/** Amplitude POSITIVA do arco, em radianos. */
export function arcSweep(arc: Arc2D): number {
  const d = arc.ccw ? arc.a1 - arc.a0 : arc.a0 - arc.a1
  const s = ((d % TWO_PI) + TWO_PI) % TWO_PI
  return s === 0 ? TWO_PI : s
}

/** Ponto do arco para um ângulo. */
export function arcPoint(arc: Arc2D, angle: number): Point2 {
  return [arc.cx + arc.r * Math.cos(angle), arc.cy + arc.r * Math.sin(angle)]
}

// --- Passo de caminho ------------------------------------------------------

/** Um passo do caminho de uma polilinha: recta ou arco. `toIndex` é o
 *  índice do vértice de chegada; num loop fechado pode ser `n`, que quer
 *  dizer "de volta a points[0]". */
export type PathStep =
  | { kind: 'line'; to: Point2; toIndex: number }
  | { kind: 'arc'; arc: Arc2D; to: Point2; toIndex: number }

export interface PathWalk {
  from: Point2
  steps: PathStep[]
  /** Índice do vértice onde o caminho acabou. */
  endIndex: number
}

/** Percorre uma polilinha em passos de recta e de arco.
 *
 *  Num loop fechado o último segmento (de points[n-1] de volta a
 *  points[0]) NÃO sai como passo, a não ser que faça parte de um arco:
 *  quem escreve fecha o caminho com o seu próprio operador (`Z` no SVG,
 *  `h` no PDF). Assim, uma polilinha sem arcos dá exactamente o mesmo
 *  caminho de sempre. */
export function walkPath(pl: Polyline2D): PathWalk | null {
  const pts = pl.points
  const n = pts.length
  if (n < 2) return null
  const at = (i: number): Point2 => pts[i === n ? 0 : i]
  const arcs = (pl.arcs ?? []).slice().sort((a, b) => a.start - b.start)
  const byStart = new Map<number, Arc2D>()
  for (const a of arcs) if (!byStart.has(a.start)) byStart.set(a.start, a)

  const steps: PathStep[] = []
  const limit = pl.closed ? n : n - 1
  let i = 0
  let guard = 0
  while (i < limit && guard++ <= n + 1) {
    const arc = byStart.get(i)
    if (arc && arc.end > i && arc.end <= n) {
      steps.push({ kind: 'arc', arc, to: at(arc.end), toIndex: arc.end })
      i = arc.end
      continue
    }
    if (pl.closed && i === n - 1) break // o fecho é do escritor
    steps.push({ kind: 'line', to: at(i + 1), toIndex: i + 1 })
    i++
  }
  return { from: pts[0], steps, endIndex: i }
}

// --- Ajuste de círculo (Kasa) ----------------------------------------------

export interface CircleFit {
  cx: number
  cy: number
  r: number
  /** Maior desvio |dist(p, centro) - r| dos pontos ajustados. */
  residual: number
}

/** Ajusta um círculo a um conjunto de pontos pelo método de Kasa.
 *  Devolve null se o sistema for degenerado (pontos colineares ou
 *  coincidentes). As coordenadas são centradas no centróide antes de
 *  resolver: com modelos georreferenciados (x na casa dos 1e6) a forma
 *  directa perdia algarismos a mais. */
export function fitCircle(points: Point2[]): CircleFit | null {
  const n = points.length
  if (n < 3) return null
  let mx = 0
  let my = 0
  for (const p of points) {
    mx += p[0]
    my += p[1]
  }
  mx /= n
  my /= n

  let sxx = 0
  let syy = 0
  let sxy = 0
  let sxz = 0
  let syz = 0
  let sz = 0
  for (const p of points) {
    const x = p[0] - mx
    const y = p[1] - my
    const z = x * x + y * y
    sxx += x * x
    syy += y * y
    sxy += x * y
    sxz += x * z
    syz += y * z
    sz += z
  }
  const det = sxx * syy - sxy * sxy
  if (!Number.isFinite(det) || Math.abs(det) < 1e-24) return null
  // [sxx sxy][D]   [-sxz]
  // [sxy syy][E] = [-syz]
  const dCoef = (-sxz * syy + syz * sxy) / det
  const eCoef = (-syz * sxx + sxz * sxy) / det
  const fCoef = -sz / n
  const cx = -dCoef / 2
  const cy = -eCoef / 2
  const rsq = cx * cx + cy * cy - fCoef
  if (!Number.isFinite(rsq) || rsq <= 0) return null
  const r = Math.sqrt(rsq)
  if (!Number.isFinite(r) || r < 1e-9 || r > 1e9) return null

  let residual = 0
  for (const p of points) {
    const d = Math.abs(Math.hypot(p[0] - mx - cx, p[1] - my - cy) - r)
    if (d > residual) residual = d
  }
  return { cx: cx + mx, cy: cy + my, r, residual }
}

// --- Detecção --------------------------------------------------------------

/** Ângulo com sinal entre dois vectores, em (-pi, pi]. */
function turn(a: Point2, b: Point2, c: Point2): number {
  const ux = b[0] - a[0]
  const uy = b[1] - a[1]
  const vx = c[0] - b[0]
  const vy = c[1] - b[1]
  if ((ux === 0 && uy === 0) || (vx === 0 && vy === 0)) return NaN
  return Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy)
}

/** Normaliza para [0, 2*pi). */
function norm2pi(a: number): number {
  const v = a % TWO_PI
  return v < 0 ? v + TWO_PI : v
}

/** Normaliza para (-pi, pi]. */
function normPi(a: number): number {
  let v = a % TWO_PI
  if (v > Math.PI) v -= TWO_PI
  if (v <= -Math.PI) v += TWO_PI
  return v
}

/** Ângulos de viragem. Num loop fechado há um por vértice (ciclicamente);
 *  numa polilinha aberta os extremos ficam NaN. */
function turns(pts: Point2[], closed: boolean): number[] {
  const n = pts.length
  const out = new Array<number>(n).fill(NaN)
  const lo = closed ? 0 : 1
  const hi = closed ? n - 1 : n - 2
  for (let i = lo; i <= hi; i++) {
    const prev = pts[(i - 1 + n) % n]
    const next = pts[(i + 1) % n]
    out[i] = turn(prev, pts[i], next)
  }
  return out
}

function stepOk(t: number): boolean {
  const a = Math.abs(t)
  return Number.isFinite(t) && a >= MIN_STEP_RAD && a <= MAX_STEP_RAD
}

/** Troços maximais de viragens do mesmo sinal, dentro dos limites e
 *  aproximadamente uniformes. Índices [a, b] inclusive sobre `t`. */
function turnRuns(t: number[], lo: number, hi: number): Array<[number, number]> {
  const runs: Array<[number, number]> = []
  let a = -1
  let sign = 0
  let min = Infinity
  let max = 0
  const close = (b: number): void => {
    if (a >= 0 && b - a + 1 >= ARC_MIN_POINTS - 2) runs.push([a, b])
    a = -1
    sign = 0
    min = Infinity
    max = 0
  }
  for (let i = lo; i <= hi; i++) {
    const v = t[i]
    if (!stepOk(v)) {
      close(i - 1)
      continue
    }
    const s = v > 0 ? 1 : -1
    const m = Math.abs(v)
    if (a >= 0 && s === sign) {
      const nMin = Math.min(min, m)
      const nMax = Math.max(max, m)
      if (nMax <= nMin * STEP_RATIO) {
        min = nMin
        max = nMax
        continue
      }
      close(i - 1)
    } else if (a >= 0) {
      close(i - 1)
    }
    a = i
    sign = s
    min = m
    max = m
  }
  close(hi)
  return runs
}

/** Roda os pontos de um loop fechado para que o índice 0 caia na maior
 *  descontinuidade de viragem. Assim nenhum arco atravessa a costura e
 *  os índices dos arcos ficam sempre crescentes. */
function seamRotation(t: number[]): number {
  const n = t.length
  let best = 0
  let bestD = -1
  for (let i = 0; i < n; i++) {
    const a = t[i]
    const b = t[(i - 1 + n) % n]
    const d =
      !Number.isFinite(a) || !Number.isFinite(b)
        ? Number.POSITIVE_INFINITY
        : Math.abs(a - b)
    if (d > bestD) {
      bestD = d
      best = i
    }
  }
  return best
}

// --- Verificação do arco contra o caminho que substitui --------------------

/** Distância de um ponto ao segmento [a, b]. */
function segDistance(p: Point2, a: Point2, b: Point2): number {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const len2 = dx * dx + dy * dy
  let t = len2 > 0 ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2 : 0
  if (t < 0) t = 0
  else if (t > 1) t = 1
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy))
}

/** Distância de um ponto a uma polilinha aberta. */
function polyDistance(p: Point2, poly: Point2[]): number {
  let best = Infinity
  for (let i = 0; i + 1 < poly.length; i++) {
    const d = segDistance(p, poly[i], poly[i + 1])
    if (d < best) best = d
  }
  return best
}

/** Número de amostras do arco na verificação. Oito chegam: o que se
 *  procura são desvios grosseiros, não décimos de milímetro. */
const ARC_SAMPLES = 8

/** Confronta o arco escolhido com o caminho ORIGINAL que ele substitui
 *  (todos os vértices, não só os cantos que entraram no ajuste).
 *
 *  Falha -> ficam segmentos. Ver a nota no topo do ficheiro. */
function arcMatchesPath(
  arc: Arc2D,
  sweep: number,
  path: Point2[],
  tol: number,
): boolean {
  if (path.length < 2) return false
  const dir = arc.ccw ? 1 : -1
  // (a) cada vértice original perto do arco, dentro da amplitude e POR
  //     ORDEM. É isto que impede o arco de percorrer o círculo pelo lado
  //     errado: um traço ao contrário põe os vértices fora da amplitude
  //     ou fora de ordem, mesmo estando todos no círculo certo.
  let last = -Infinity
  for (const p of path) {
    const a = Math.atan2(p[1] - arc.cy, p[0] - arc.cx)
    let d = norm2pi((a - arc.a0) * dir)
    if (d > sweep + Math.PI) d -= TWO_PI
    if (d < -1e-6 || d > sweep + 1e-6) return false
    if (d < last - 1e-9) return false
    last = d
    if (Math.abs(Math.hypot(p[0] - arc.cx, p[1] - arc.cy) - arc.r) > tol) {
      return false
    }
  }
  // (b) o arco desenhado não se afasta da polilinha original.
  for (let i = 1; i < ARC_SAMPLES; i++) {
    const a = arc.a0 + dir * sweep * (i / ARC_SAMPLES)
    const s: Point2 = [
      arc.cx + arc.r * Math.cos(a),
      arc.cy + arc.r * Math.sin(a),
    ]
    if (polyDistance(s, path) > tol) return false
  }
  return true
}

/** Contexto de um ajuste: os cantos (`pts`), o sítio de cada canto na
 *  polilinha completa (`idx`) e a própria polilinha completa. */
interface FitContext {
  pts: Point2[]
  idx: number[]
  orig: Point2[]
}

/** Parâmetros do arco de um troço de vértices já validado. Devolve null
 *  se o ajuste não servir (resíduo, sentido inconsistente, amplitude
 *  fora do razoável ou desvio do caminho original). */
function arcFrom(ctx: FitContext, start: number, end: number): Arc2D | null {
  const { pts, idx, orig } = ctx
  const n = pts.length
  const at = (i: number): Point2 => pts[i % n]
  const run: Point2[] = []
  for (let i = start; i <= end; i++) run.push(at(i))
  if (run.length < ARC_MIN_POINTS) return null
  const fit = fitCircle(run)
  if (!fit) return null
  if (fit.residual > arcTolerance(fit.r)) return null

  // Amplitude a partir do CENTRO ajustado: soma dos passos angulares.
  // Todos têm de ter o mesmo sinal, senão o troço vai e vem.
  const ang = run.map((p) => Math.atan2(p[1] - fit.cy, p[0] - fit.cx))
  let sweep = 0
  let sign = 0
  let maxStep = 0
  let minStep = Infinity
  for (let i = 0; i + 1 < ang.length; i++) {
    const d = normPi(ang[i + 1] - ang[i])
    if (!Number.isFinite(d) || d === 0) return null
    const s = d > 0 ? 1 : -1
    if (sign === 0) sign = s
    else if (s !== sign) return null
    const m = Math.abs(d)
    // O mesmo limite das viragens, agora sobre o círculo ajustado: sem
    // ele a flecha admitida em baixo não teria tecto.
    if (m > MAX_STEP_RAD) return null
    if (m > maxStep) maxStep = m
    if (m < minStep) minStep = m
    sweep += d
  }
  // A tesselação de uma curva é UNIFORME. Esta é a mesma razão do
  // `STEP_RATIO` das viragens, aplicada agora ao círculo ajustado, e é o
  // que distingue os dois casos que geometricamente são iguais: um
  // vértice a meio da corda de uma curva grosseira, e um vértice a meio
  // de uma RECTA longa. No primeiro os passos angulares são todos
  // parecidos; no segundo há passos minúsculos (a curva a sério) ao lado
  // de um passo enorme (a recta que o ajuste tentou engolir).
  if (maxStep > minStep * STEP_RATIO) return null
  const mag = Math.abs(sweep)
  if (mag < 0.02 || mag > MAX_ARC_SWEEP) return null

  const arc: Arc2D = {
    start,
    end,
    cx: fit.cx,
    cy: fit.cy,
    r: fit.r,
    a0: norm2pi(ang[0]),
    a1: norm2pi(ang[ang.length - 1]),
    ccw: sweep > 0,
  }

  // Caminho original substituído, com os vértices colineares incluídos:
  // são eles que denunciam um ajuste que saltou por cima de uma recta.
  const nOrig = orig.length
  const from = idx[start]
  const to = end >= idx.length ? nOrig : idx[end]
  const path: Point2[] = []
  for (let i = from; i <= to; i++) path.push(orig[i % nOrig])

  // Tolerância = a do ajuste + a flecha da maior corda do troço, que é o
  // desvio que a tesselação tem por construção.
  const tol = arcTolerance(fit.r) + fit.r * (1 - Math.cos(maxStep / 2))
  if (!arcMatchesPath(arc, mag, path, tol)) return null
  return arc
}

/** Tenta ler o loop TODO como um círculo. */
function wholeCircle(pts: Point2[], t: number[]): Circle2D | null {
  const n = pts.length
  if (n < CIRCLE_MIN_POINTS) return null
  let min = Infinity
  let max = 0
  let sum = 0
  let sign = 0
  for (let i = 0; i < n; i++) {
    const v = t[i]
    if (!stepOk(v)) return null
    const s = v > 0 ? 1 : -1
    if (sign === 0) sign = s
    else if (s !== sign) return null
    const m = Math.abs(v)
    if (m < min) min = m
    if (m > max) max = m
    sum += v
  }
  if (max > min * STEP_RATIO) return null
  if (Math.abs(Math.abs(sum) - TWO_PI) > TURN_SUM_TOL) return null
  const fit = fitCircle(pts)
  if (!fit) return null
  if (fit.residual > arcTolerance(fit.r)) return null
  return { cx: fit.cx, cy: fit.cy, r: fit.r }
}

/** Para cada índice, o sinal da viragem significativa mais próxima
 *  ANTES e DEPOIS dele. Num loop fechado a procura dá a volta (duas
 *  passagens chegam). Serve para distinguir um vértice colinear
 *  BANAL de um ponto de INFLEXÃO. */
function neighbourSigns(t: number[], closed: boolean): [Int8Array, Int8Array] {
  const n = t.length
  const prev = new Int8Array(n)
  const next = new Int8Array(n)
  const sig = (v: number): number =>
    Number.isFinite(v) && Math.abs(v) >= COLLINEAR_RAD ? (v > 0 ? 1 : -1) : 0
  const passes = closed ? 2 : 1
  let s = 0
  for (let k = 0; k < passes; k++) {
    for (let i = 0; i < n; i++) {
      prev[i] = s
      const c = sig(t[i])
      if (c !== 0) s = c
    }
  }
  s = 0
  for (let k = 0; k < passes; k++) {
    for (let i = n - 1; i >= 0; i--) {
      next[i] = s
      const c = sig(t[i])
      if (c !== 0) s = c
    }
  }
  return [prev, next]
}

/** Índices dos vértices que definem mesmo a forma: fora os repetidos e
 *  os que estão em cima da recta dos vizinhos. Numa polilinha aberta os
 *  extremos ficam sempre.
 *
 *  EXCEPÇÃO: um vértice com viragem desprezável mas com curvaturas de
 *  SINAIS OPOSTOS de um lado e do outro é um ponto de INFLEXÃO e nunca
 *  se deita fora. Numa curva em S bem tesselada a viragem na inflexão é
 *  exactamente zero (as tangentes coincidem); se esse vértice
 *  desaparecesse, os dois arcos ficavam vizinhos no array reduzido, a
 *  mudança de sentido passava despercebida e o ajuste atravessava-a. */
export function cornerIndices(pts: Point2[], closed: boolean): number[] {
  const n = pts.length
  const t = turns(pts, closed)
  const [prevSign, nextSign] = neighbourSigns(t, closed)
  const out: number[] = []
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n]
    if (i > 0 && Math.abs(pts[i][0] - prev[0]) < 1e-12 && Math.abs(pts[i][1] - prev[1]) < 1e-12) {
      continue // repetido
    }
    if (!closed && (i === 0 || i === n - 1)) {
      out.push(i)
      continue
    }
    const v = t[i]
    if (Number.isFinite(v) && Math.abs(v) < COLLINEAR_RAD) {
      const inflection =
        prevSign[i] !== 0 && nextSign[i] !== 0 && prevSign[i] !== nextSign[i]
      if (!inflection) continue
    }
    out.push(i)
  }
  return out
}

/** Reconhece círculos e arcos numa polilinha.
 *
 *  Devolve a MESMA polilinha quando não há nada a reconhecer (identidade
 *  referencial, para não gerar lixo). Quando há, devolve uma cópia com
 *  `circle` ou `arcs` preenchidos; os `points` continuam a ser a
 *  tesselação completa, portanto tudo o que não sabe de arcos (caixa
 *  envolvente, recorte, selecção pelo desenho, medição) não muda. Os
 *  índices dos arcos apontam para os `points`, e os vértices colineares
 *  que caiam DENTRO de um arco são simplesmente saltados. */
export function fitArcs(pl: Polyline2D): Polyline2D {
  const n0 = pl.points.length
  // Guarda: um loop fechado curto (hexágono, rectângulo) nem se analisa.
  if (pl.closed && n0 < CIRCLE_MIN_POINTS) return pl
  if (n0 < ARC_MIN_POINTS) return pl
  for (const p of pl.points) {
    if (!Number.isFinite(p[0]) || !Number.isFinite(p[1])) return pl
  }

  if (pl.closed) {
    let points = pl.points
    let idx = cornerIndices(points, true)
    if (idx.length < CIRCLE_MIN_POINTS) return pl
    let rp = idx.map((i) => points[i])
    let t = turns(rp, true)

    const circle = wholeCircle(rp, t)
    if (circle) return { ...pl, circle, arcs: undefined }

    // Roda os pontos ORIGINAIS para a costura ficar no índice 0: assim
    // nenhum arco precisa de dar a volta ao princípio do array, e o
    // índice `n` de fecho aponta mesmo para points[0].
    const k = idx[seamRotation(t)]
    if (k !== 0) {
      points = [...pl.points.slice(k), ...pl.points.slice(0, k)]
      idx = cornerIndices(points, true)
      if (idx.length < ARC_MIN_POINTS) return pl
      rp = idx.map((i) => points[i])
      t = turns(rp, true)
    }
    // A viragem do índice 0 é a costura: ignora-se de propósito.
    const arcs = remap(
      collect({ pts: rp, idx, orig: points }, t, 1, rp.length - 1, true),
      idx,
      points.length,
    )
    if (arcs.length === 0) return pl
    return { ...pl, points, arcs, circle: undefined }
  }

  const idx = cornerIndices(pl.points, false)
  if (idx.length < ARC_MIN_POINTS) return pl
  const rp = idx.map((i) => pl.points[i])
  const t = turns(rp, false)
  const arcs = remap(
    collect({ pts: rp, idx, orig: pl.points }, t, 1, rp.length - 2, false),
    idx,
    pl.points.length,
  )
  if (arcs.length === 0) return pl
  return { ...pl, arcs, circle: undefined }
}

/** Converte índices do array reduzido (só cantos) para os `points`. */
function remap(arcs: Arc2D[], idx: number[], n: number): Arc2D[] {
  return arcs.map((a) => ({
    ...a,
    start: idx[a.start],
    end: a.end >= idx.length ? n : idx[a.end],
  }))
}

/** Converte troços de viragem em arcos.
 *
 *  Dentro de cada troço candidato faz-se uma varredura gulosa: abre-se a
 *  menor janela válida e estica-se enquanto o ajuste aguentar. Se a
 *  janela inicial não servir, avança-se um vértice. É preciso ser assim
 *  (e não só encolher pela ponta) porque a viragem do vértice de JUNÇÃO
 *  entre dois arcos costuma parecer-se com as do arco seguinte, e entra
 *  no troço sem lhe pertencer. */
function collect(
  ctx: FitContext,
  t: number[],
  lo: number,
  hi: number,
  closed: boolean,
): Arc2D[] {
  const n = ctx.pts.length
  const maxVertex = closed ? n : n - 1
  const out: Arc2D[] = []
  for (const [a, b] of turnRuns(t, lo, hi)) {
    const last = Math.min(b + 1, maxVertex)
    let s = a - 1
    while (s + ARC_MIN_POINTS - 1 <= last) {
      let e = s + ARC_MIN_POINTS - 1
      let best = arcFrom(ctx, s, e)
      if (!best) {
        s++
        continue
      }
      while (e + 1 <= last) {
        const next = arcFrom(ctx, s, e + 1)
        if (!next) break
        best = next
        e++
      }
      out.push(best)
      s = e
    }
  }
  return out
}

/** Aplica o reconhecimento a uma lista de polilinhas. */
export function fitArcsAll(polys: Polyline2D[]): Polyline2D[] {
  let changed = false
  const out = polys.map((p) => {
    const q = fitArcs(p)
    if (q !== p) changed = true
    return q
  })
  return changed ? out : polys
}

/** Deita fora os arcos reconhecidos, ficando a tesselação pura.
 *
 *  É a rede de segurança do utilizador ("Arcos suaves" desligado) e é
 *  BARATA de propósito: os `points` nunca deixaram de ser a polilinha
 *  completa, portanto desligar é só apagar dois campos, sem regerar o
 *  desenho. Devolve o próprio desenho quando não há arcos nenhuns. */
export function stripArcs(d: Drawing2D): Drawing2D {
  let touched = false
  const clean = (polys: Polyline2D[]): Polyline2D[] => {
    let hit = false
    const out = polys.map((p) => {
      if (!p.arcs && !p.circle) return p
      hit = true
      const { arcs: _a, circle: _c, ...rest } = p
      return rest
    })
    if (!hit) return polys
    touched = true
    return out
  }
  const layers = d.layers.map((l) => {
    const cut = clean(l.cut)
    const projection = clean(l.projection)
    return cut === l.cut && projection === l.projection
      ? l
      : { ...l, cut, projection }
  })
  return touched ? { ...d, layers } : d
}
